import { Button, CircularProgress, Dialog, DialogActions, DialogContent, Grid, Paper } from '@material-ui/core';
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Alert } from "@mui/material";
import Authorize from 'components/Authorize/Authorize';
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import DropdownFilter from "components/DropdownFilter";
import NotificationProvider from "components/NotificationProvider";
import { activityStatuses } from "features/activity/activityConsts";
import { useGetAllActivityItemsQuery, useGetAllActivityTypesQuery, useGetActivityItemMaterialsQuery } from 'features/activity/activitySlice';
import { useGetAllBundlesQuery } from 'features/activityBundle/activityBundleSlice';
import { ActivityItemPricingType } from "features/pricingManagement/PricingManagementConsts";
import CustomInput from "../../../../../components/CustomInput";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useGetVendorsQuery } from 'features/vendor/vendorSlice';
import { selectIsClient, selectUserClientAccountId, userHasOneClient } from 'features/user/userSlice';
import { IconCar } from 'icons';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import FormatService from "services/formatService";
import LoadingSpinner from '../../../../../components/LoadingSpinner/LoadingSpinner';
import { useAddActivityMutation } from '../../../../activity/activitySlice';
import { useGetLotsQuery } from '../../../../lots/lotSlice';
import SingleNote from '../../../../notes/SingleNote';
import { useGetActivityPricingByAssetIdsQuery } from "../../../../pricing/pricingSlice";
import { userHasOneClientAccount } from '../../../../user/userSlice';
import ActivityType from './ActivityType/ActivityType';
import AssetsSelector from './Assets/AssetsSelector';
import { resetFilters, setFilters } from './Assets/assetsFiltersSlice';
import { CreateWorkOrderStyles } from './CreateWorkOrderStyles';
import ActivityPricing from './Pricing/ActivityPricing';
import { isAuthorized } from 'components/Authorize/authorizeUtil';
import { arrayToObject } from 'utils/object-util';

const EMPTY_ARRAY = [];

const CreateWorkOrderPage = ({ open, setOpen }) => {
    const classes = CreateWorkOrderStyles();
    const isNarrowScreen = useMediaQuery("(max-width: 1600px)");

    const dispatch = useDispatch();
    const { filters } = useSelector(state => state.assetsFilters);
    const isClient = useSelector(selectIsClient);
    const hasOneClaim = useSelector(userHasOneClient);
    const clientAccountId = useSelector(selectUserClientAccountId);
    const hasOneAccount = useSelector(userHasOneClientAccount);
    const selectedClientAccount = hasOneAccount ? parseInt(clientAccountId?.[0]) : !!filters?.['clientAccountId']?.value ? parseInt(filters?.['clientAccountId']?.value) : 0;
    let { data: clients, error: clientsError, isLoading: isLoadingClients } = useGetClientsQuery();
    const clientAccounts = clients?.flatMap(c => c.clientAccounts);
    const preselectedAssets = filters?.['preselectedAssets']?.value;
    const getClientId = (accId) => {
        return clientAccounts?.find(a => a.id == accId)?.clientId
    }

    let { data: lots, error: lotError, isLoading: isLoadingLots } = useGetLotsQuery(true);
    let { data: vendors, error: vendorsError, isLoading: isLoadingVendors } = useGetVendorsQuery(getClientId(selectedClientAccount));
    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: activityTypes, error: activityTypesError, isFetching: isLoadingActivityTypes } = useGetAllActivityTypesQuery();
    let { data: bundles, error: bundlesError, isFetching: isLoadingBundles } = useGetAllBundlesQuery();

    lots = lots || [];
    activityItems = activityItems || [];
    activityTypes = activityTypes || [];
    vendors = vendors || [];
    bundles = bundles || [];

    const [assets, setAssets] = useState([]);
    const [assetIds, setAssetIds] = useState(null);
    const [lot, setLot] = useState(null);
    const [note, setNote] = useState({ content: '', viewByClient: true });
    const [vendor, setVendor] = useState(null);
    const [activity, setActivity] = useState(null);
    const [bundleId, setBundleId] = useState(null);
    const [customActivity, setCustomActivity] = useState(null);
    const [activityType, setActivityType] = useState(null);
    const [repairOrder, setRepairOrder] = useState('');
    const [pricing, setPricing] = useState([]);
    const [errors, setErrors] = useState({});
    const [saveWasClicked, setSaveWasClicked] = useState(false);
    const [materials, setMaterials] = useState([]);

    const { data: availableMaterials = EMPTY_ARRAY, isLoading: isLoadingAvailableMaterials } = useGetActivityItemMaterialsQuery(
        activity,
        { skip: !activity || !!bundleId }
    );

    let bundlesLookup = arrayToObject(bundles);
    let activityItemsLookup = arrayToObject(activityItems);
    let activityTypesLookup = arrayToObject(activityTypes);

    let currentActivityItem = activityItemsLookup?.[activity];

    let { data: prices, error: pricesError, isLoading: isLoadingPrices, refetch: refetchPricing } = useGetActivityPricingByAssetIdsQuery({
        activityItemId: bundleId ? null : activity,
        bundleId: bundleId,
        assetIds: assetIds,
        vendorId: vendor,
        clientId: getClientId(isClient ? clientAccountId : filters['clientAccountId']?.value),
        materialsUsed: materials,
        includeCost: isAuthorized(permissionProfiles.ACTIVITY.EDIT_ACTIVITY_INTERNAL_FIELDS)
    }, { skip: (!activity && !bundleId) || !(assetIds?.length > 0) });

    const [addActivity, { isLoading: isLoadingSave }] = useAddActivityMutation();

    const onChangeClient = (prop, filterValue) => {
        setAssets([]);
        dispatch(setFilters({ ...filters, ...{ [prop]: { value: filterValue?.id ?? '' } } }));
    };

    const handleClose = () => {
        setOpen(false);
        setAssets([]);
        setAssetIds(null);
        setLot(null);
        setNote({ content: '', viewByClient: true });
        setVendor(null);
        setActivityType(null);
        setActivity(null);
        setBundleId(null);
        setRepairOrder('');
        setCustomActivity(null);
        setMaterials([]);
        setPricing([]);
        setErrors({});
        setSaveWasClicked(false);
        dispatch(setFilters({ ...filters, ['clientId']: '', ['clientAccountId']: '' }));
        dispatch(resetFilters());
    }

    const handleNoteChange = (field, value) => {
        setNote({ ...note, [field]: value });
    }

    const handleSelectAssets = (assets) => {
        setAssets(assets);
        const hasAssets = assets?.length > 0;
        //if only one asset is selected and lot wasn't selected yet, select it's lot by default
        if (assets?.length === 1 && lot == null) {
            setLot(assets[0]?.lotId);
        } else if (!hasAssets) {
            setLot(null);
        }

        setAssetIds(hasAssets ? assets.map(s => s.id) : null);
    }

    const isCustomActivity = () => {
        if (!activity) {
            return false;
        }

        let activityItem = activityItemsLookup?.[activity];

        return activityItem?.isCustom;
    };

    const getPricing = () => {
        const pricing = [];

        assets?.map(asset => {
            let priceForType = prices
                ?.filter(p => p.assetId == asset.id)?.[0]
                ?.activityPrices?.[0];
            let internalCost = priceForType?.internalCost ?? 0;
            let materialsCost = priceForType?.materialsCost ?? 0;

            let price =
                internalCost == null && materialsCost == null
                    ? null
                    : (internalCost ?? 0) + (materialsCost ?? 0);

            let revenue = priceForType?.charge ?? 0;

            const activityPricing = {
                vehicleId: asset?.id,
                price: price ?? null,
                revenue: revenue ?? null,
                priceUnavailable: price == null || price === 0,
                revenueUnavailable: revenue == null || revenue === 0
            };

            pricing.push(activityPricing);
        });

        return pricing;
    }

    const checkNotSupported = () => {
        //find if any combination isn't supported
        let checkedNotSupported = false;

        prices?.forEach(price => {
            price?.activityPrices?.forEach(ap => {
                if (ap.notSupported) {
                    checkedNotSupported = true;
                }
            })
        });

        return checkedNotSupported;
    }

    const validate = () => {
        const msgs = {};
        let valid = true;

        if (!(assets?.length > 0)) {
            valid = false;
            msgs['assets'] = 'Assets are required';
        }

        // if (!lot) {
        //     valid = false;
        //     msgs['lot'] = 'Lot is required';
        // }

        if (!bundleId && !activityType) {
            valid = false;
            msgs['activityType'] = 'Activity Type is required';
        }

        if (!activity && !bundleId) {
            valid = false;
            msgs['activity'] = 'Activity or Bundle is required';
        }

        if (isCustomActivity() && !customActivity) {
            valid = false;
            msgs['customActivity'] = 'Custom Activity is required';
        }

        if (checkNotSupported()) {
            valid = false;
            msgs['notSupported'] = 'One or more of the combination are not supported';
        }

        setErrors(msgs);
        return valid;
    }

    const handleSave = async () => {
        setSaveWasClicked(true);
        if (!validate()) {
            return;
        }

        let date = new Date(moment().format("MMM DD, yyyy, hh:mm a") + " UTC");
        let activityDate = date.toISOString();

        const materialsToSend = materials
            .filter(m => m.numberOfUnits > 0)
            .map(m => ({
                activityItemMaterialId: m.activityItemMaterialId,
                numberOfUnits: m.numberOfUnits
            }));

        let data = {
            activityTypeId: activityType,
            activityItemId: bundleId ? null : activity,
            bundleId: bundleId,
            activityId: isClient ? activityStatuses.RequestedByClient : activityStatuses.PendingApproval,
            vehicleInfo: assets?.map(a => ({ vehicleId: a.id, lotId: a.lotId })),
            vendorId: vendor,
            //lotId: lot,
            price: 0.0,
            revenue: 0.0,
            activityPricings: getPricing(),
            activityDate,
            note: note,
            customActivity,
            materials: materialsToSend.length > 0 ? materialsToSend : null,
            repairOrder: repairOrder
        };

        let result = await addActivity(data);
        if (result && !result.error) {
            NotificationProvider.success("Work order created successfully");
            handleClose();
        } else {
            NotificationProvider.error("Something happened, try again or contact support");
        }
    }

    const computeCostRevenue = (activityPrices) => {
        const unavailableMsg = 'Pricing and Availability varies based on location. ParkMyFleet will reach out to you soon with more information';
        let cost = unavailableMsg;
        let revenue = unavailableMsg;
        let consumableMsg = '';

        if (!activityPrices || activityPrices.length === 0) {
            return { cost, revenue, consumableMsg };
        }

        // Check if any activityPrice has useInventory true
        const useInventory = activityPrices.some(ap => ap.useInventory);

        if (activityPrices.length > 1) {
            // Range for cost
            const costs = activityPrices.map(ap => ap.internalCost + (ap.materialsCost ?? 0)).filter(v => v != null && v !== 0);
            if (costs.length > 0) {
                const min = Math.min(...costs);
                const max = Math.max(...costs);
                cost = min === max ? FormatService.formatCurrency(min) : `${FormatService.formatCurrency(min)} - ${FormatService.formatCurrency(max)}`;
            } else {
                cost = unavailableMsg;
            }
            // Range for revenue
            const revenues = activityPrices.map(ap => ap.charge).filter(v => v != null && v !== 0);
            if (revenues.length > 0) {
                const min = Math.min(...revenues);
                const max = Math.max(...revenues);
                revenue = min === max ? FormatService.formatCurrency(min) : `${FormatService.formatCurrency(min)} - ${FormatService.formatCurrency(max)}`;
            } else {
                revenue = unavailableMsg;
            }
        } else {
            const priceForType = activityPrices[0];
            const costRaw = priceForType?.internalCost + (priceForType?.materialsCost ?? 0);
            const revenueRaw = priceForType?.charge;
            cost = (costRaw == null || costRaw === 0) ? unavailableMsg : FormatService.formatCurrency(costRaw);
            revenue = (revenueRaw == null || revenueRaw === 0) ? unavailableMsg : FormatService.formatCurrency(revenueRaw);
        }

        // If there is a price (not unavailableMsg) and useInventory is true, add the message
        const hasPrice = (cost !== unavailableMsg || revenue !== unavailableMsg);
        if (hasPrice && useInventory) {
            consumableMsg = 'Pricing depends on consumables';
        }

        return { cost, revenue, consumableMsg };
    };

    useEffect(() => {
        if (preselectedAssets?.length) {
            setAssets(preselectedAssets);
            setAssetIds(preselectedAssets.map(a => a.id));
        }

        if (open) {
            setPricing([]);
        }
    }, [preselectedAssets, open]);

    useEffect(() => {
        if (saveWasClicked)
            validate();
    }, [assets, lot, activity, activityType, customActivity, vendor, prices]);

    useEffect(() => {
        if (prices?.length && activity && (assetIds?.length > 0) && vendor)
            refetchPricing();
        else {
            setPricing([]);
        }
    }, [assets, activity, lot, assetIds, vendor, clientAccountId, filters?.['clientAccountId']?.value]);

    useEffect(() => {
        //set pricing array
        const pricingArr = [];
        assets?.map(asset => {
            const priceObj = prices?.find(p => p.assetId == asset.id);
            const activityPrices = priceObj?.activityPrices || [];
            const descriptor = " " + (asset.descriptor ?? asset.assetId);
            const notSupported = activityPrices.some(ap => ap.notSupported);

            pricingArr.push({ descriptor, ...computeCostRevenue(activityPrices), notSupported });
        })
        setPricing(pricingArr);
    }, [prices, isLoadingPrices, assets]);

    useEffect(() => {
        if (!activity || isLoadingAvailableMaterials) {
            setMaterials(prev => prev.length === 0 ? prev : []);
            return;
        }

        const initializedMaterials = availableMaterials.map(availMat => ({
            activityItemMaterialId: availMat.id,
            materialName: availMat.materialName,
            unitOfMeasure: availMat.unitOfMeasure || '',
            numberOfUnits: 0
        }));

        setMaterials(initializedMaterials);
    }, [activity, availableMaterials, isLoadingAvailableMaterials]);

    const handleMaterialUnitsChange = (index, value) => {
        const updatedMaterials = [...materials];
        updatedMaterials[index] = {
            ...updatedMaterials[index],
            numberOfUnits: parseFloat(value) || 0
        };
        setMaterials(updatedMaterials);
    };

    const isLoading = isLoadingLots || isLoadingVendors || isLoadingActivityTypes || isLoadingActivityItems || isLoadingBundles || isLoadingSave || isLoadingPrices || isLoadingAvailableMaterials || isLoadingClients;

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth='xl'>
            <DialogContent>
                <LoadingSpinner loading={isLoading} />
                <Grid container direction="row" spacing={2}>

                    <Grid item xs={12}>
                        <Paper className={classes.card}>
                            <Grid container display="felx" alignItems='center'>
                                <Grid item className={classes.titleIcon}>
                                    <IconCar />
                                </Grid>
                                <Grid item className={classes.title}>
                                    Create Work Order
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {!hasOneClaim && <Grid item xs={12}>
                        <Paper className={classes.card}>
                            <div className={classes.sectionTitle}>
                                Select Client
                            </div>
                            <DropdownFilter
                                values={clientAccounts}
                                onSelect={(val) => onChangeClient('clientAccountId', val)}
                                value={filters?.['clientAccountId']?.value}
                                title={!filters?.['clientAccountId']?.value && 'Client'}
                                showEmpty
                                withTypeAhead
                                newAutoCompleteStyle
                            />
                            {!filters?.['clientAccountId']?.value &&
                                <Grid xs={12} className={classes.alertWrapper}>
                                    <Alert severity='error' >
                                        Client is required
                                    </Alert>
                                </Grid>}
                        </Paper>
                    </Grid>}

                    <Grid item xs={12}>
                        <Paper className={classes.card}>
                            <div className={classes.sectionTitle}>
                                Select Assets
                            </div>
                            <AssetsSelector
                                assets={assets}
                                setAssets={handleSelectAssets}
                                isNarrowScreen={isNarrowScreen}
                                disabled={(!filters['clientAccountId']?.value) && !isClient}
                            />
                            {!!errors['assets'] &&
                                <Grid xs={12} className={classes.alertWrapper}>
                                    <Alert severity='error' >
                                        {errors['assets']}
                                    </Alert>
                                </Grid>}
                        </Paper>
                    </Grid>

                    {/* <Grid item xs={12}>
                        <Grid container spacing={2} >
                            <Grid item xs={4}>
                                <Paper className={classes.card}>
                                    <div className={classes.sectionTitle}>
                                        Select A Location
                                    </div>
                                    <LotSelector
                                        lots={lots}
                                        clientId={selectedClient}
                                        isLoading={isLoadingLots}
                                        lot={lot}
                                        setLot={setLot}
                                        isNarrowScreen={isNarrowScreen}
                                    />
                                    {!!errors['lot'] &&
                                        <Grid xs={12} className={classes.alertWrapper}>
                                            <Alert severity='error' >
                                                {errors['lot']}
                                            </Alert>
                                        </Grid>}
                                </Paper>
                            </Grid>

                            <Grid item xs={8}>
                                <Paper className={classes.card}>
                                    <div className={classes.mapWrapper}>
                                        <LotMap
                                            lots={lots}
                                            lot={lot}
                                            setLot={setLot}
                                        />
                                    </div>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid> */}

                    <Grid item xs={12}>
                        <Grid container spacing={2} >
                            <Grid item xs={4}>
                                <Paper className={classes.card}>
                                    <div className={classes.sectionTitle}>
                                        Select an Activity
                                    </div>
                                    <Grid container direction='row' spacing={2}>
                                        <Grid item xs={12}>
                                            <ActivityType
                                                activity={activity}
                                                setActivity={setActivity}
                                                bundleId={bundleId}
                                                setBundleId={setBundleId}
                                                customActivity={customActivity}
                                                setCustomActivity={setCustomActivity}
                                                isCustomActivity={isCustomActivity}
                                                setActivityType={setActivityType}
                                                errors={errors}
                                                repairOrder={repairOrder}
                                                setRepairOrder={setRepairOrder}
                                            />
                                        </Grid>
                                        {/* <Grid item xs={12}>
                                            <WorkOrderVendorView
                                                vendor={vendor}
                                                setVendor={setVendor}
                                                vendors={vendors}
                                                isNarrowScreen={isNarrowScreen}
                                            />
                                        </Grid> */}
                                        {!bundleId && currentActivityItem?.pricingType === ActivityItemPricingType.BASE_PLUS_MATERIALS && materials.length > 0 && (
                                            <Grid item xs={12} className={classes.materialsContainer}>
                                                <h4 className={classes.materialsTitle}>Materials Usage</h4>
                                                {materials.map((material, index) => (
                                                    <div key={`${material.activityItemMaterialId}-${index}`} className={classes.materialRow}>
                                                        <div className={classes.materialInputWrapper}>
                                                            <CustomInput
                                                                id={`material-units-${index}`}
                                                                elementType="input"
                                                                numberOnly={true}
                                                                value={material.numberOfUnits > 0 ? material.numberOfUnits : ''}
                                                                placeholder="0"
                                                                onChange={(val) => handleMaterialUnitsChange(index, val)}
                                                            />
                                                        </div>
                                                        <div className={classes.materialLabel}>
                                                            {material.materialName} {material.unitOfMeasure ? (material.numberOfUnits > 1 ? `${material.unitOfMeasure}s` : material.unitOfMeasure) : ''}
                                                        </div>
                                                    </div>
                                                ))}
                                            </Grid>
                                        )}
                                    </Grid>
                                    {!!errors['notSupported'] &&
                                        <Grid xs={12} className={classes.alertWrapper}>
                                            <Alert severity='error' >
                                                {errors['notSupported']}
                                            </Alert>
                                        </Grid>}
                                </Paper>
                            </Grid>

                            <Grid item xs={8}>
                                <Paper className={classes.card}>
                                    <div className={classes.sectionTitle}>
                                        Notes
                                    </div>
                                    <SingleNote
                                        note={note}
                                        handleNoteChange={handleNoteChange}
                                    />
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper className={classes.summaryCard}>
                            <Grid container direction="row" spacing={1} >
                                <Grid item xs={12}>
                                    <Grid container display="felx" alignItems='center'>
                                        <Grid item className={classes.titleIcon}>
                                            <IconCar />
                                        </Grid>
                                        <Grid item className={classes.title}>
                                            Work Order Summary
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <div className={classes.sectionTitle}>
                                        Assets
                                    </div>
                                    <AssetsSelector
                                        assets={assets}
                                        disabled={true}
                                    />
                                </Grid>

                                {/* <Grid item xs={12}>
                                    <Grid item xs={isNarrowScreen ? 10 : 4}>
                                        <div className={classes.sectionTitle}>
                                            Location
                                        </div>
                                        {lot > 0 ? <LotSelectorItem
                                            option={lots?.find(a => a.id == parseInt(lot))}
                                            isNarrowScreen
                                        /> : <div className={classes.summaryItem} />}
                                    </Grid>
                                </Grid>

                                <Grid item xs={isNarrowScreen ? 10 : 4}>
                                    <div className={classes.sectionTitle}>
                                        Vendor
                                    </div>
                                    {Vendor > 0 ? <VendorSelectorItem
                                        option={vendors?.find(a => a.id == parseInt(vendor))}
                                        isNarrowScreen
                                    /> : <div className={classes.summaryItem} />}
                                </Grid> */}

                                {!!bundleId ? (
                                    <Grid item xs={12}>
                                        <div className={classes.sectionTitle}>
                                            Bundle
                                        </div>
                                        <div className={classes.summaryItemBorder}>
                                            {bundlesLookup?.[parseInt(bundleId)]?.name}
                                        </div>
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item xs={12}>
                                            <div className={classes.sectionTitle}>
                                                Activity Type
                                            </div>
                                            <div className={classes.summaryItemBorder}>
                                                {activityTypesLookup?.[activityType]?.name}
                                            </div>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <div className={classes.sectionTitle}>
                                                Activity
                                            </div>
                                            <div className={classes.summaryItemBorder}>
                                                {activityItems?.find(a => a.id == parseInt(activity))?.name}
                                            </div>
                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={12}>
                                    <div className={classes.sectionTitle}>
                                        Activity
                                    </div>
                                    <div className={classes.summaryItemBorder}>
                                        {activityItems?.find(a => a.id == parseInt(activity))?.name}
                                    </div>
                                </Grid>
                                <Grid item xs={12}>
                                    <div className={classes.sectionTitle}>
                                        Repair Order
                                    </div>
                                    <div className={classes.summaryItemBorder}>
                                        {repairOrder}
                                    </div>
                                </Grid>

                                <Grid item xs={12}>
                                    <div className={classes.sectionTitle}>
                                        Notes
                                    </div>
                                    <div dangerouslySetInnerHTML={{ __html: note?.content }} className={classes.summaryItemBorder} />
                                </Grid>

                                {!bundleId &&
                                    <Authorize profile={permissionProfiles.DASHBOARD.WORKORDER_CLIENT_FILTER}>
                                        <Grid item xs={12}>
                                            <div className={classes.sectionTitle}>
                                                Estimated Cost
                                            </div>
                                            <div className={classes.summaryItemBorder}>
                                                {(!vendors?.length) ?
                                                    'Pricing and Availability varies based on location. ParkMyFleet will reach out to you soon with more information' :
                                                    <ActivityPricing pricing={pricing} cost />}
                                            </div>
                                        </Grid>
                                    </Authorize>}

                                <Grid item xs={12}>
                                    <div className={classes.sectionTitle}>
                                        Estimated Charge
                                    </div>
                                    <div className={classes.summaryItemBorder}>
                                        <ActivityPricing pricing={pricing} />
                                    </div>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                </Grid>
            </DialogContent>
            <DialogActions>
                {isLoading && <CircularProgress size={'1em'} />}
                <div className={classes.actionWrapper}>
                    <Button
                        className={classes.actionButton}
                        onClick={handleClose}
                        color='primary'
                        variant='outlined'
                    >
                        Cancel
                    </Button>
                    <Button
                        className={classes.actionButton}
                        disabled={isLoading}
                        onClick={handleSave}
                        color='primary'
                        variant='contained'
                    >
                        Save
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}
export default CreateWorkOrderPage;
